import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import Layout from "@/components/Layout";
import { pricing, fmtC, fmtH } from "@/lib/calc";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ChevronLeft, FileDown, Bot, Cloud } from "lucide-react";
import { toast } from "sonner";
import SummaryTab from "@/components/estimation/SummaryTab";
import WbsTab from "@/components/estimation/WbsTab";
import LoadTab from "@/components/estimation/LoadTab";
import RateCardTab from "@/components/estimation/RateCardTab";
import PricingTab from "@/components/estimation/PricingTab";
import AuditTab from "@/components/estimation/AuditTab";
import BaselineTab from "@/components/estimation/BaselineTab";
import TimelineTab from "@/components/estimation/TimelineTab";
import ChatPanel from "@/components/estimation/ChatPanel";

import SalesforceTab from "@/components/estimation/SalesforceTab";

const STAGES = ["lead", "estimating", "submitted", "won", "lost"];

const SF_STAGE_CLS = {
  default: "bg-zinc-100 text-zinc-700",
  won: "bg-emerald-50 text-emerald-700 border border-emerald-200",
  lost: "bg-red-50 text-red-700 border border-red-200",
  active: "bg-blue-50 text-[#002FA7] border border-blue-200",
};
const sfBadgeClass = (sf) => {
  if (!sf) return SF_STAGE_CLS.default;
  if (sf.is_won) return SF_STAGE_CLS.won;
  if (sf.is_closed) return SF_STAGE_CLS.lost;
  return SF_STAGE_CLS.active;
};

export default function OpportunityPage() {
  const { id } = useParams();
  const [opp, setOpp] = useState(null);
  const [est, setEst] = useState(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [sfBadge, setSfBadge] = useState(null);

  const reload = async () => {
    const [o, e] = await Promise.all([
      api.get(`/opportunities/${id}`),
      api.get(`/opportunities/${id}/estimation`),
    ]);
    setOpp(o.data);
    setEst(e.data);
    // Lightly fetch SF stage for the header badge — silent on failure (e.g. unlinked).
    api.get(`/opportunities/${id}/salesforce`)
      .then((r) => setSfBadge(r.data?.data || null))
      .catch(() => setSfBadge(null));
  };

  useEffect(() => { reload().catch((e) => toast.error(apiError(e))); }, [id]); // eslint-disable-line

  const setStage = async (stage) => {
    setOpp({ ...opp, stage });
    try { await api.put(`/opportunities/${id}`, { stage }); } catch (e) { toast.error(apiError(e)); }
  };

  const exportExcel = async () => {
    try {
      const res = await api.get(`/opportunities/${id}/export`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(opp.name || "estimation").replace(/[^\w ._-]+/g, "")}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Excel exported");
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!opp || !est) return <Layout><p className="p-8 text-sm text-zinc-500">Loading…</p></Layout>;

  const p = pricing(est);
  const kpis = [
    { label: "Resource load hrs", value: fmtH(p.loadHours) },
    { label: "Cost basis", value: fmtC(p.subtotal) },
    { label: "WBS suggested", value: fmtC(p.suggestedCost) },
    { label: "Final price", value: fmtC(p.finalPrice), accent: true },
  ];

  return (
    <Layout>
      <div className="p-6 md:p-8">
        <Link to="/" data-testid="back-to-pipeline" className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-[#002FA7] mb-4">
          <ChevronLeft size={13} strokeWidth={1.5} /> Pipeline
        </Link>
        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] font-medium text-zinc-500 mb-1">{opp.client || "Opportunity"}</p>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 data-testid="opportunity-title" className="font-heading text-3xl font-medium tracking-tighter">{opp.name}</h1>
              {sfBadge && (
                <span data-testid="opp-sf-stage-badge"
                  title={`Salesforce stage: ${sfBadge.stage}${sfBadge.probability != null ? ` (${sfBadge.probability}% probability)` : ""}`}
                  className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wide font-medium rounded-sm px-2.5 py-1 ${sfBadgeClass(sfBadge)}`}>
                  <Cloud size={11} strokeWidth={1.5} /> SF · {sfBadge.stage}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="open-chat-button" onClick={() => setChatOpen(true)}
              className="flex items-center gap-1.5 bg-zinc-900 hover:bg-zinc-800 text-white text-sm px-3 py-2 rounded-sm">
              <Bot size={14} strokeWidth={1.5} /> AI Assistant
            </button>
            <button data-testid="export-excel-button" onClick={exportExcel}
              className="flex items-center gap-1.5 border border-zinc-300 hover:bg-zinc-50 text-sm px-3 py-2 rounded-sm">
              <FileDown size={14} strokeWidth={1.5} /> Export Excel
            </button>
            <select data-testid="opportunity-stage-select" value={opp.stage} onChange={(e) => setStage(e.target.value)}
              className="text-xs border border-zinc-300 rounded-sm px-2.5 py-2 bg-white outline-none focus:border-[#002FA7] uppercase tracking-wide">
              {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-200 border border-zinc-200 rounded-sm overflow-hidden mb-8">
          {kpis.map((k) => (
            <div key={k.label} className={`p-4 ${k.accent ? "bg-[#002FA7] text-white" : "bg-white"}`}>
              <p className={`text-[10px] uppercase tracking-[0.2em] font-medium ${k.accent ? "text-blue-200" : "text-zinc-500"}`}>{k.label}</p>
              <p data-testid={`kpi-${k.label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="font-mono text-xl mt-1">{k.value}</p>
            </div>
          ))}
        </div>

        <Tabs defaultValue="wbs">
          <TabsList className="bg-zinc-100 rounded-sm h-9 p-0.5 mb-6">
            {[["summary", "Summary"], ["wbs", "WBS Estimate"], ["timeline", "Timeline"], ["load", "Resource Load"], ["rates", "Rate Card"], ["pricing", "Pricing & Risk"], ["baseline", "Baseline"], ["salesforce", "Salesforce"], ["audit", "Audit"]].map(([v, l]) => (
              <TabsTrigger key={v} value={v} data-testid={`tab-${v}`}
                className="rounded-sm text-xs px-4 data-[state=active]:bg-white data-[state=active]:text-[#002FA7] data-[state=active]:shadow-none">
                {l}
              </TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value="summary"><SummaryTab est={est} /></TabsContent>
          <TabsContent value="wbs"><WbsTab est={est} setEst={setEst} oppId={id} reload={reload} /></TabsContent>
          <TabsContent value="timeline"><TimelineTab est={est} setEst={setEst} oppId={id} /></TabsContent>
          <TabsContent value="load"><LoadTab est={est} setEst={setEst} oppId={id} /></TabsContent>
          <TabsContent value="rates"><RateCardTab est={est} setEst={setEst} oppId={id} /></TabsContent>
          <TabsContent value="pricing"><PricingTab est={est} setEst={setEst} oppId={id} /></TabsContent>
          <TabsContent value="baseline"><BaselineTab est={est} oppId={id} /></TabsContent>
          <TabsContent value="salesforce"><SalesforceTab oppId={id} sfDomain={process.env.REACT_APP_SF_DOMAIN || "https://resolvetechsolutions.lightning.force.com/lightning/r/Opportunity"} /></TabsContent>
          <TabsContent value="audit"><AuditTab oppId={id} /></TabsContent>
        </Tabs>
        <ChatPanel oppId={id} open={chatOpen} onClose={() => setChatOpen(false)} onEstimation={setEst} />
      </div>
    </Layout>
  );
}
